/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2024.
 */

import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { FlowboxContentpageRv } from '@/components/content/FlowboxContentpageRv';
import { StoreLocatorAutocomplete } from '@/components/content/StoreLocator/parts/Autocomplete';
import { StoreLocatorMarker } from '@/components/content/StoreLocator/parts/Marker';
import { StoreLocatorSideList } from '@/components/content/StoreLocator/parts/SideList';
import { storeLocatorSideListElementSX } from '@/components/content/StoreLocator/styles/SideList/element';
import { googleMapSx, storeLocatorGridContainerSX } from '@/components/content/StoreLocator/styles/SideList/gridContainer';
import { storeLocatorSideListTitleTypographySX } from '@/components/content/StoreLocator/styles/SideList/titleTypography';
import { storeLocatorMapContainerSX } from '@/components/content/StoreLocator/styles/mapContainer';
import { useStoreLocator } from '@/data/Content/StoreLocator';
import { useLocalization } from '@/data/Localization';
import { DEFAULT_LOCATION, GOOGLE_MAP_ZOOM } from '@/data/constants/storeLocator';
import { ContentProvider } from '@/data/context/content';
import { Order } from '@/data/types/Order';
import { Container, Grid, ListItemText, Paper, Typography } from '@mui/material';
import { GoogleMap, MarkerClusterer } from '@react-google-maps/api';
import { FC, useEffect, useMemo, useState } from 'react';
import { StoreLocatorInfoBox } from './parts/InfoBox';
import { useGoogleMapsAPILocale } from '@/data/state/useGoogleMapsAPILocale';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useDeviceType } from '@/data/deviceDetection';
import { useStoreLocatorPage } from '@/data/Content/StoreLocatorPage';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { useSettings } from '@/data/Settings';
import { STATIC_HOST_SECURE } from '@/data/config/STATIC_HOST_SECURE';
import { MARKER_CLUSTER_STYLES } from '@/utils/getClusterStyles';

export const StoreLocatorBase: FC<{ order?: Order }> = ({ order }) => {
	const router = useNextRouter();
	const { locale } = router;
	const { loadedLocale } = useGoogleMapsAPILocale();
	const {settings} = useSettings();
	const localization = useLocalization('StoreLocator');
	const {isDesktop} = useDeviceType();
	const [mapCenter, setMapCenter] = useState(DEFAULT_LOCATION);
		
	const [zoom, setZoom] = useState(GOOGLE_MAP_ZOOM.INIT);
	const staticHostSecure = useMemo(()=>settings?.userData?.StaticHostSecure??STATIC_HOST_SECURE,[settings])

	const {storesByCity, cityURLs} = useStoreLocatorPage();
	const stores = useMemo(()=>storesByCity ? Object.values(storesByCity).flat():[],[storesByCity])

	const {
		data,
		locator,
		setLocator,
		setCurrentLocation,
		searchLatLng,
		showDirection,
		destination,
		directionResults,
		searchTextFieldRef,
		currentLocation,
		clickedIndex,
		searchTerm,
		clearSearch,
		mapOnLoad,
		directionsCallback,
		findNearStore,
		onPlaceChanged,
		searchBoxOnLoad,
		onListItemClick,
		...rest
	} = useStoreLocator();

	useEffect(() => {
		setCurrentLocation(DEFAULT_LOCATION);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps
	const flowboxStLocKey = "N47qOLZnQvC8h_6BPsp6-w";

	const [showMap, setShowMap] = useState(false);
	useEffect(()=>{
		if(locale && locale === loadedLocale.locale){
			setTimeout(()=>setShowMap(true),1000);
		}
	},[ locale, loadedLocale]);
	
	return (
		<ContentProvider
			value={{
				clickedIndex,
				searchTerm,
				searchTextFieldRef,
				currentLocation,
				findNearStore,
				searchBoxOnLoad,
				onPlaceChanged,
				onListItemClick,
				clearSearch,
				order,
				storesByCity,
				stores,
				cityURLs,
				staticHostSecure,
				setMapCenter,
				setZoom,
				...rest,
			}}
		>
			<Grid container>
				<Grid item sx={{ paddingBottom: '26px'}}>
					<ContentRecommendation
						id='stLocMainHeaderEspot-rv'
						properties={{ emsName: 'stLocMainHeaderEspot-rv' }}
					/>
				</Grid>
			</Grid>
			<Grid
				container
				justifyContent="center"
				alignItems="stretch"
				spacing={2}
				paddingBottom={4}
				sx={storeLocatorGridContainerSX}
			>
				{isDesktop &&
					<Grid item xs md={4}>
						<Paper sx={{ borderRadius: 0, padding: 1 , height:'100%'}}>
							<Grid container justifyContent="space-between" alignItems="flex-end" spacing={1}>
								<Grid item flex={1}>
									<ListItemText
										sx={storeLocatorSideListElementSX}
										primary={<Typography variant="subtitle2" sx={storeLocatorSideListTitleTypographySX}>
											{localization.searchStlocStore.t()}
										</Typography>} />
								</Grid>
							</Grid>
								<StoreLocatorAutocomplete />
								<StoreLocatorSideList />
						</Paper>
					</Grid>
				}

				{!isDesktop &&<Container><StoreLocatorAutocomplete /></Container> }

				{showMap ?
					<Grid 
						id="storesListMainMap"
						item xs 
					>
						<Grid sx={googleMapSx}>
							<GoogleMap
								mapContainerStyle={{ height: '150vh', width: '100%', maxHeight:720, minHeight: 589}}
								zoom={zoom}
								center={mapCenter}
								onLoad={mapOnLoad}
							>
								<MarkerClusterer
									options={{
            							styles: MARKER_CLUSTER_STYLES,
          							}}
									children={(clusterer)=>
										<>
											{stores?.map((store, index)=>(
												<StoreLocatorMarker key={store.id} store={store} index={index} clusterer={clusterer} />
											))}
										</>
									}
								/>
							</GoogleMap>
						</Grid>
						<Grid item>
							<StoreLocatorInfoBox />
						</Grid>
					</Grid>
					:
					<Grid item xs md={8} minHeight={'80vh'}>
						<ProgressIndicator />
					</Grid>
				}
				
				{!isDesktop &&
					<Grid item xs md={4} sx={isDesktop ? storeLocatorMapContainerSX : null}>
						<Paper sx={{borderRadius:0}}>							
							<StoreLocatorSideList />
						</Paper>
					</Grid>
				}
			</Grid>
			<Grid > <FlowboxContentpageRv id={''} stLocFlowKey={flowboxStLocKey} /> </Grid>
			<Grid>
				<ContentRecommendation
					id='stLocMainFooterEspot-rv'
					properties={{ emsName: 'stLocMainFooterEspot-rv' }}
				/>
			</Grid>
		</ContentProvider>
	);
};
